// Growth › Campaigns › Schedule (/growth/schedule?day=YYYY-MM-DD): every email
// going out, week by week, and what went on the days before. It replaced Today's
// batch (owner, November 3): adding a campaign to the schedule approves what it
// plans, so nothing here asks for approval. Pause a campaign on its page; remove
// or move one email here.
//
// A week as a list, at every width (owner, November 3: "a list weekly view …
// you can see the list of names rather than a calendar view"): each day, Monday
// first, with who each email goes to, when, from which campaign, and what
// happened to it. ?day= is any day in the week. Clicking an email opens it, the
// subject and the whole body as it goes or went, with Remove and Move while it
// waits. Replies that asked to stop sit on top: the address is already
// suppressed, and a person should read what they said.

import { useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { api } from '@/api'
import { errorMessage, isApiError } from '@/api/client'
import type { ReviewItem, ScheduleDay, ScheduleEmail, ScheduleEmailDetail, ScheduleResponse } from '@/api/growthCampaignTypes'
import { PageHeader } from '@/components/shell/PageHeader'
import {
  Badge,
  Button,
  Card,
  CardHeader,
  ChevronLeftIcon,
  ChevronRightIcon,
  Dialog,
  ErrorState,
  FormField,
  InlineAlert,
  LoadingState,
  RelativeTime,
  SkeletonRows,
  TextInput,
  useToast,
} from '@/components/ui'
import { cn, formatDateTime } from '@/lib/format'
import { SCHEDULE_STATE_LABELS, SCHEDULE_STATE_TONE, sendDetail } from '@/lib/growthCampaigns'
import { useDocumentTitle } from '@/lib/hooks'
import { invalidateQueries, useQuery } from '@/lib/query'
import { SendingNotice, SendingSettingsDialog } from './sendingShared'
import { addDays, dayLabel, daysFrom, timeIn, validDay, weekStart } from '@/lib/schedule'

function goingOut(d: ScheduleDay): number {
  return d.scheduled + d.sending
}

/** What a day says: what goes out from today on, what happened before. */
function dayLines(d: ScheduleDay, today: string): string[] {
  const lines: string[] = []
  if (d.day >= today && goingOut(d) > 0) lines.push(`${goingOut(d)} going out`)
  if (d.sent) lines.push(`${d.sent} sent`)
  if (d.dry_run) lines.push(`${d.dry_run} dry run`)
  if (d.failed) lines.push(`${d.failed} failed`)
  if (d.day < today && goingOut(d) > 0) lines.push(`${goingOut(d)} waiting`)
  return lines
}

function Review({ items, onDone }: { items: ReviewItem[]; onDone: (fn: () => Promise<unknown>) => void }) {
  if (items.length === 0) return null
  return (
    <Card>
      <CardHeader title="Replies to review" description="They said something that reads like stop, so the address is already on the do-not-contact list. Read it in contact@scorelm.com if it looks like a mistake." />
      <ul className="grid gap-3">
        {items.map((item) => (
          <li key={item.id} className="flex flex-wrap items-start justify-between gap-2 border-t border-line pt-3 first:border-t-0 first:pt-0">
            <div className="grid min-w-0 flex-1 gap-0.5">
              <p className="text-[13.5px] font-semibold text-ink">
                {item.lead ? (
                  <Link to={`/growth/leads/${item.lead.id}`} className="hover:text-accent">
                    {item.lead.name}
                  </Link>
                ) : (
                  (item.email ?? 'Deleted lead')
                )}
              </p>
              {/* A stranger's words, shown as text. */}
              {item.snippet ? <p className="break-words text-[13px] text-muted">“{item.snippet}”</p> : null}
              <RelativeTime value={item.received_at ?? item.created_at} className="text-xs text-subtle" />
            </div>
            <Button size="sm" onClick={() => onDone(() => api.growth.schedule.markReviewed(item.id))}>
              Reviewed
            </Button>
          </li>
        ))}
      </ul>
    </Card>
  )
}

function whenText(e: ScheduleEmail, timeZone: string): string {
  if (e.at) return timeIn(e.at, e.kind === 'campaign' && e.window ? e.window.time_zone : timeZone)
  if (e.window && e.status === 'scheduled') return `${e.window.start}–${e.window.end}`
  return '—'
}

function whatText(e: ScheduleEmail): string {
  if (e.kind === 'one_off') return `One-off${e.signer_name ? `, from ${e.signer_name}` : ''}`
  const step = e.step_number === 1 ? 'first email' : `follow-up ${(e.step_number ?? 2) - 1}`
  return `${e.campaign?.name ?? 'Campaign'} · ${step}`
}

function StateBadge({ e }: { e: Pick<ScheduleEmail, 'status'> }) {
  return <Badge tone={SCHEDULE_STATE_TONE[e.status]}>{SCHEDULE_STATE_LABELS[e.status]}</Badge>
}

/** One email: when, who (by name, then address), from what, and what happened. Each part once, laid out for the width. */
function EmailRow({ e, timeZone, onOpen }: { e: ScheduleEmail; timeZone: string; onOpen: (e: ScheduleEmail) => void }) {
  const detail = sendDetail(e)
  return (
    <li className="grid grid-cols-[4.5rem_minmax(0,1fr)_auto] items-start gap-x-3 gap-y-1 px-4 py-2.5 sm:grid-cols-[5.5rem_minmax(0,1.3fr)_minmax(0,1fr)_9rem_auto] sm:items-center">
      <span className="tabular row-span-3 pt-0.5 text-[13px] text-muted sm:row-span-1 sm:pt-0">{whenText(e, timeZone)}</span>
      <span className="col-start-2 row-start-1 grid min-w-0 gap-0.5">
        <span className="break-words text-[13.5px] font-semibold text-ink">{e.recipient}</span>
        <span className="font-mono text-xs font-medium text-muted [overflow-wrap:anywhere]">{e.to}</span>
      </span>
      <span className="col-start-2 row-start-2 break-words text-xs text-muted sm:col-start-3 sm:row-start-1 sm:text-[13px]">{whatText(e)}</span>
      <span className="col-start-2 row-start-3 flex flex-wrap items-center gap-1.5 sm:col-start-4 sm:row-start-1 sm:grid sm:justify-items-start sm:gap-0.5">
        <StateBadge e={e} />
        {detail ? <span className="text-xs text-muted">{detail}</span> : null}
      </span>
      <Button size="sm" variant="ghost" className="col-start-3 row-start-1 sm:col-start-5" aria-label={`Open the email to ${e.recipient}`} onClick={() => onOpen(e)}>
        Open
      </Button>
    </li>
  )
}

/** The week, Monday to Sunday: each day with its emails by name, or a short line when it has none. */
function WeekView({ from, data, timeZone, onOpen }: { from: string; data: ScheduleResponse; timeZone: string; onOpen: (e: ScheduleEmail) => void }) {
  const to = addDays(from, 6)
  const { data: list, error, status, refetch } = useQuery(['growth', 'schedule-emails', from, to], (signal) => api.growth.schedule.emails({ from, to }, signal), { keepPrevious: true })
  const current = list?.from === from ? list : null
  const days = daysFrom(from, to)

  if (status === 'error' && !current) return <ErrorState error={error} title="Couldn't load this week's emails" onRetry={refetch} />
  if (!current) return <SkeletonRows rows={5} label="Loading the week's emails" />

  return (
    <ol aria-label={`Emails, week of ${dayLabel(from, { year: true })}`} className="divide-y divide-line">
      {days.map((day) => {
        const emails = current.emails.filter((e) => e.day === day)
        const counts = data.days.find((d) => d.day === day)
        const lines = counts ? dayLines(counts, data.today) : []
        const label = dayLabel(day)
        const isToday = day === data.today
        return (
          <li key={day} aria-label={`${label}${isToday ? ', today' : ''}: ${lines.length ? lines.join(', ') : 'nothing'}`} className={cn(isToday && 'bg-accent-soft/30')}>
            <div className={cn('flex flex-wrap items-baseline justify-between gap-x-3 gap-y-0.5 px-4 pt-3', emails.length === 0 && 'pb-3')}>
              <h3 className={cn('text-[13.5px] font-bold', day < data.today ? 'text-muted' : 'text-ink')}>
                {label}
                {isToday ? <span className="ml-2 rounded bg-accent px-1.5 py-0.5 text-2xs font-bold uppercase tracking-eyebrow text-white">Today</span> : null}
              </h3>
              <span className={cn('text-xs', lines.some((l) => l.endsWith('failed')) ? 'text-bad' : 'text-muted')}>{lines.length ? lines.join(' · ') : 'No emails'}</span>
            </div>
            {emails.length > 0 ? (
              <ul aria-label={`Emails on ${label}`} className="mt-1 divide-y divide-line/70 pb-1">
                {emails.map((e) => (
                  <EmailRow key={`${e.kind}-${e.id}`} e={e} timeZone={timeZone} onOpen={onOpen} />
                ))}
              </ul>
            ) : null}
          </li>
        )
      })}
      {current.truncated ? <li className="px-4 py-2 text-xs text-muted">Showing the first 500 of each kind.</li> : null}
    </ol>
  )
}

function EmailDialog({ target, timeZone, onClose }: { target: ScheduleEmail | null; timeZone: string; onClose: () => void }) {
  const toast = useToast()
  const key = target ? ['growth', 'schedule-email', target.kind, target.id] : null
  const { data: email, error, status, refetch } = useQuery(key, (signal) => api.growth.schedule.open(target!.kind, target!.id, signal))
  const [mode, setMode] = useState<'view' | 'remove' | 'move'>('view')
  const [moveDay, setMoveDay] = useState('')
  const [moveTime, setMoveTime] = useState('')
  const [busy, setBusy] = useState(false)
  const [problem, setProblem] = useState<string | null>(null)

  const close = () => {
    setMode('view')
    setProblem(null)
    onClose()
  }
  const act = async (fn: () => Promise<ScheduleEmailDetail>, done: string) => {
    setBusy(true)
    setProblem(null)
    try {
      await fn()
      invalidateQueries('growth')
      setMode('view')
      toast.success(done)
    } catch (err) {
      setProblem(isApiError(err) && err.code === 'not_scheduled' ? err.message : errorMessage(err))
    } finally {
      setBusy(false)
    }
  }
  const startMove = (e: ScheduleEmailDetail) => {
    setMoveDay(e.day)
    setMoveTime(e.kind === 'one_off' && e.at ? new Date(e.at).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', timeZone }) : '')
    setMode('move')
  }

  const e = email && target && email.id === target.id && email.kind === target.kind ? email : null
  const when = e ? (e.at ? formatDateTime(e.at) : e.window ? `${dayLabel(e.day)}, in the window ${e.window.start}–${e.window.end} (${e.window.time_zone})` : dayLabel(e.day)) : ''

  return (
    <Dialog
      open={!!target}
      onClose={close}
      size="lg"
      title={e?.subject || (target ? `Email to ${target.recipient}` : '')}
      description={e ? <span className="break-words">To {e.to} · From {e.from} · {when}</span> : undefined}
      footer={
        e && (e.can_remove || e.can_move) && mode === 'view' ? (
          <>
            {e.can_move ? <Button onClick={() => startMove(e)}>Move to another day…</Button> : null}
            {e.can_remove ? (
              <Button variant="danger" onClick={() => setMode('remove')}>
                Remove from the schedule…
              </Button>
            ) : null}
          </>
        ) : undefined
      }
    >
      {status === 'loading' && !e ? (
        <LoadingState label="Opening the email…" />
      ) : status === 'error' && !e ? (
        <ErrorState error={error} title="Couldn't open this email" onRetry={refetch} />
      ) : e ? (
        <div className="grid gap-3">
          <div className="flex flex-wrap items-center gap-2 text-xs text-muted">
            <StateBadge e={e} />
            {e.campaign ? (
              <Link to={`/growth/campaigns/${e.campaign.id}`} className="underline underline-offset-2 hover:text-accent" onClick={close}>
                {whatText(e)}
              </Link>
            ) : (
              <span>{whatText(e)}</span>
            )}
            {e.lead ? (
              <Link to={`/growth/leads/${e.lead.id}`} className="underline underline-offset-2 hover:text-accent" onClick={close}>
                {e.lead.name}
              </Link>
            ) : null}
            {e.replied_at ? <Badge tone="good">Replied</Badge> : null}
            {e.clicked_at ? <Badge tone="neutral">Clicked</Badge> : null}
            {e.bounced_at ? <Badge tone="bad">{e.bounce_kind === 'soft' ? 'Soft bounce' : 'Bounced'}</Badge> : null}
            {e.unsubscribed_at ? <Badge tone="outline">Unsubscribed</Badge> : null}
          </div>
          {sendDetail(e) ? <p className="text-xs text-muted">{sendDetail(e)}</p> : null}
          {e.problem ? <InlineAlert tone="warn">{e.problem}</InlineAlert> : null}
          <p className="text-xs text-muted">
            {e.rendered === 'as_sent'
              ? 'As it went. Links show where they lead; in the email each was its own link.'
              : e.rendered === 'as_it_will_go'
                ? 'As it will go: it was written when it was scheduled.'
                : "As it would go now. It's written again when it goes, with that day's values."}
            {e.approved_by ? ` Approved when ${e.approved_by.name ?? e.approved_by.email} added the campaign to the schedule.` : ''}
          </p>
          <pre data-testid="schedule-email-text" className="max-h-[50vh] max-w-full overflow-y-auto overflow-x-hidden whitespace-pre-wrap break-words rounded-md border border-line p-3 font-sans text-[13px] text-ink">
            {e.text || 'No text: the lead was deleted.'}
          </pre>
          {problem ? <InlineAlert tone="bad">{problem}</InlineAlert> : null}
          {mode === 'remove' ? (
            <InlineAlert
              tone="warn"
              title="Remove this email from the schedule?"
              action={
                <span className="flex gap-1.5">
                  <Button size="sm" disabled={busy} onClick={() => setMode('view')}>
                    Keep it
                  </Button>
                  <Button size="sm" variant="danger" disabled={busy} onClick={() => void act(() => api.growth.schedule.remove(e.kind, e.id), 'Removed from the schedule.')}>
                    Remove
                  </Button>
                </span>
              }
            >
              {e.kind === 'campaign' ? "It won't go, and its place that day goes to the next lead. The rest of the campaign carries on." : "It won't go."}
            </InlineAlert>
          ) : null}
          {mode === 'move' ? (
            <div className="grid gap-3 rounded-md border border-line p-3">
              <div className="grid gap-3 sm:grid-cols-2">
                <FormField label="Move to">{({ id }) => <TextInput id={id} type="date" value={moveDay} onChange={(ev) => setMoveDay(ev.target.value)} />}</FormField>
                {e.kind === 'one_off' ? (
                  <FormField label="Time" hint={`In ${timeZone}.`}>
                    {({ id }) => <TextInput id={id} type="time" value={moveTime} onChange={(ev) => setMoveTime(ev.target.value)} />}
                  </FormField>
                ) : (
                  <p className="self-end text-xs text-muted">It gets a time in the campaign's window on that day.</p>
                )}
              </div>
              <div className="flex justify-end gap-1.5">
                <Button size="sm" disabled={busy} onClick={() => setMode('view')}>
                  Cancel
                </Button>
                <Button
                  size="sm"
                  variant="primary"
                  disabled={busy || !validDay(moveDay)}
                  onClick={() => void act(() => api.growth.schedule.move(e.kind, e.id, { day: moveDay, ...(e.kind === 'one_off' && moveTime ? { time: moveTime } : {}) }), `Moved to ${dayLabel(moveDay)}.`)}
                >
                  Move
                </Button>
              </div>
            </div>
          ) : null}
        </div>
      ) : null}
    </Dialog>
  )
}

export default function SchedulePage() {
  useDocumentTitle('Schedule')
  const [searchParams, setSearchParams] = useSearchParams()
  const dayParam = validDay(searchParams.get('day'))
  const range = dayParam ? { from: weekStart(dayParam), to: addDays(weekStart(dayParam), 6) } : null
  const schedule = useQuery(['growth', 'schedule', range?.from ?? 'today', range?.to ?? ''], (signal) => api.growth.schedule.get(range ?? {}, signal), { keepPrevious: true })
  const data = schedule.data
  const selected = dayParam ?? data?.today ?? null
  const from = selected ? weekStart(selected) : null
  const [opened, setOpened] = useState<ScheduleEmail | null>(null)
  const [problem, setProblem] = useState<string | null>(null)
  const [settingsOpen, setSettingsOpen] = useState(false)
  const sending = useQuery(['growth', 'sending'], (signal) => api.growth.sending.get(signal))

  const pick = (day: string) => setSearchParams((prev) => {
    const next = new URLSearchParams(prev)
    next.set('day', day)
    return next
  })
  const act = async (fn: () => Promise<unknown>) => {
    setProblem(null)
    try {
      await fn()
      invalidateQueries('growth')
    } catch (err) {
      setProblem(errorMessage(err))
    }
  }

  // The counts on screen must cover the week shown; a fresh range is loading otherwise.
  const covers = !!data && !!from && data.from <= from && addDays(from, 6) <= data.to
  const thisWeek = !!data && !!from && from === weekStart(data.today)
  const weekTotals = data && from
    ? data.days.filter((d) => d.day >= from && d.day <= addDays(from, 6)).reduce(
        (t, d) => ({ ...t, out: t.out + (d.day >= data.today ? goingOut(d) : 0), sent: t.sent + d.sent, dry: t.dry + d.dry_run, failed: t.failed + d.failed }),
        { out: 0, sent: 0, dry: 0, failed: 0 },
      )
    : null
  const totals = weekTotals
    ? [weekTotals.out && `${weekTotals.out} going out`, weekTotals.sent && `${weekTotals.sent} sent`, weekTotals.dry && `${weekTotals.dry} dry run`, weekTotals.failed && `${weekTotals.failed} failed`].filter(Boolean).join(' · ')
    : ''

  return (
    <div className="grid grid-cols-1 gap-5">
      <PageHeader
        title="Schedule"
        description={
          <>
            Every email going out, week by week. Adding a campaign to the schedule approves what it plans; pause it on its page, or remove or move one email here.{' '}
            <Link to="/growth/campaigns" className="underline underline-offset-2 hover:text-accent">
              Campaigns
            </Link>
          </>
        }
        actions={<Button onClick={() => setSettingsOpen(true)}>Sending settings…</Button>}
      />
      {sending.data ? <SendingNotice s={sending.data} /> : null}
      {problem ? <InlineAlert tone="bad">{problem}</InlineAlert> : null}
      {data ? <Review items={data.review} onDone={(fn) => void act(fn)} /> : null}

      {schedule.status === 'error' && !data ? (
        <Card>
          <ErrorState error={schedule.error} title="Couldn't load the schedule" onRetry={schedule.refetch} />
        </Card>
      ) : !data || !from || !covers ? (
        <Card flush>
          <SkeletonRows rows={5} label="Loading the schedule" />
        </Card>
      ) : (
        <Card flush className="overflow-hidden">
          <div className="flex flex-wrap items-center justify-between gap-2 border-b border-line px-4 py-3">
            <div className="grid gap-0.5">
              <h2 className="text-base font-bold text-ink">{weekLabel(from)}</h2>
              <p className="text-xs text-muted">
                {totals || 'No emails this week'} · Times in {data.time_zone}, or a campaign's own zone
              </p>
            </div>
            <div className="flex items-center gap-1.5">
              <Button size="sm" variant="ghost" icon={<ChevronLeftIcon size={15} />} aria-label="Previous week" onClick={() => pick(addDays(from, -7))} />
              <Button size="sm" disabled={thisWeek} onClick={() => pick(data.today)}>
                This week
              </Button>
              <Button size="sm" variant="ghost" icon={<ChevronRightIcon size={15} />} aria-label="Next week" onClick={() => pick(addDays(from, 7))} />
            </div>
          </div>
          <WeekView from={from} data={data} timeZone={data.time_zone} onOpen={setOpened} />
        </Card>
      )}
      <EmailDialog target={opened} timeZone={data?.time_zone ?? 'UTC'} onClose={() => setOpened(null)} />
      <SendingSettingsDialog open={settingsOpen} onClose={() => setSettingsOpen(false)} />
    </div>
  )
}

/** "Nov 16 – 22, 2026", or across months "Nov 30 – Dec 6, 2026". */
function weekLabel(from: string): string {
  const to = addDays(from, 6)
  const md = (d: string, month: boolean) => new Date(`${d}T12:00:00Z`).toLocaleDateString(undefined, { ...(month ? { month: 'short' } : {}), day: 'numeric', timeZone: 'UTC' })
  return `${md(from, true)} – ${md(to, to.slice(5, 7) !== from.slice(5, 7))}, ${to.slice(0, 4)}`
}
